/**
 * Zitadel sign-in for this tab (OWA-1137): Authorization Code with PKCE through oauth4webapi.
 *
 * The client asks for the Practice project audience and, once a firm is chosen, the roles scope of
 * that one firm (`urn:zitadel:iam:org:roles:id:{firmOrgId}`), so the access token's roles claim
 * names exactly that firm, as the Rust server requires (design 4.3). Switching firms is a new,
 * silent (`prompt=none`) authorization. Transaction state and the refresh token live in this
 * tab's own storage under the session namespace, like the Auth0 SDK cache before it.
 *
 * @module client/Auth/zitadelClient
 */

import * as oauth from 'oauth4webapi';
import {SessionCredentialError, zitadelRolesClaim, type TabCredentials} from './tabSession';

export interface ZitadelSettings {
    /** `https://<instance host>`, without a trailing slash, exactly as Zitadel publishes `iss`. */
    issuer: string;
    clientId: string;
    /** The Practice project: the access token's audience and the roles claim's name. */
    projectId: string;
    redirectUri: string;
}

export interface AuthorizeOptions {
    /** The firm whose roles the tokens carry; omitted on first sign-in, before a firm is chosen. */
    firmOrgId?: string;
    prompt?: 'login' | 'none';
    loginHint?: string;
    returnTo?: string;
}

/** A firm the person may pick: the org ID (the firmId) and the org's primary domain. */
export interface FirmChoice {
    firmOrgId: string;
    domain: string;
}

interface Transaction {
    verifier: string;
    state: string;
    nonce: string;
    returnTo?: string;
}

export interface CallbackResult {
    credentials: TabCredentials;
    returnTo?: string;
}

/** The scopes of one authorization: identity, refresh, the project audience and, when a firm is
 * chosen, that firm's roles. */
export function zitadelScopes(projectId: string, firmOrgId?: string): string {
    return ['openid', 'profile', 'email', 'offline_access', `urn:zitadel:iam:org:project:id:${projectId}:aud`,
        ...(firmOrgId ? [`urn:zitadel:iam:org:roles:id:${firmOrgId}`] : [])].join(' ');
}

/** The firms a token's roles claim names, for the picker. Read from an ID token issued without a
 * roles scope, which names every firm where the person holds a Practice role. */
export function firmChoices(idTokenClaims: Record<string, unknown>, projectId: string): FirmChoice[] {
    const roles = idTokenClaims[zitadelRolesClaim(projectId)];
    if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return [];
    const domains = new Map<string, string>();
    for (const byOrg of Object.values(roles as Record<string, unknown>)) {
        if (!byOrg || typeof byOrg !== 'object' || Array.isArray(byOrg)) continue;
        for (const [org, domain] of Object.entries(byOrg as Record<string, unknown>)) {
            if (!domains.has(org)) domains.set(org, typeof domain === 'string' ? domain : org);
        }
    }
    return [...domains].map(([firmOrgId, domain]) => ({firmOrgId, domain}))
        .sort((a, b) => a.domain.localeCompare(b.domain));
}

export class ZitadelClient {
    private server?: Promise<oauth.AuthorizationServer>;
    private readonly client: oauth.Client;
    private readonly transactionKey: string;
    private readonly refreshKey: string;

    constructor(
        private readonly settings: ZitadelSettings,
        private readonly storage: Storage,
        namespace: string,
        private readonly fetcher: typeof fetch = globalThis.fetch.bind(globalThis),
    ) {
        this.client = {client_id: settings.clientId};
        this.transactionKey = `${namespace}:zitadel:transaction`;
        this.refreshKey = `${namespace}:zitadel:refresh`;
    }

    private options() {
        return {[oauth.customFetch]: this.fetcher};
    }

    private discover(): Promise<oauth.AuthorizationServer> {
        if (!this.server) {
            const issuer = new URL(this.settings.issuer);
            this.server = oauth.discoveryRequest(issuer, {algorithm: 'oidc', ...this.options()})
                .then(response => oauth.processDiscoveryResponse(issuer, response))
                .catch(error => { this.server = undefined; throw error; });
        }
        return this.server;
    }

    /** The authorization URL; the PKCE verifier, state and nonce are stored for the callback. */
    async authorizationUrl(opts: AuthorizeOptions = {}): Promise<string> {
        const as = await this.discover();
        if (!as.authorization_endpoint) throw new Error('The sign-in service publishes no authorization endpoint.');
        const verifier = oauth.generateRandomCodeVerifier();
        const transaction: Transaction = {verifier, state: oauth.generateRandomState(),
            nonce: oauth.generateRandomNonce(), returnTo: opts.returnTo};
        this.storage.setItem(this.transactionKey, JSON.stringify(transaction));
        const url = new URL(as.authorization_endpoint);
        url.searchParams.set('client_id', this.settings.clientId);
        url.searchParams.set('redirect_uri', this.settings.redirectUri);
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('scope', zitadelScopes(this.settings.projectId, opts.firmOrgId));
        url.searchParams.set('code_challenge', await oauth.calculatePKCECodeChallenge(verifier));
        url.searchParams.set('code_challenge_method', 'S256');
        url.searchParams.set('state', transaction.state);
        url.searchParams.set('nonce', transaction.nonce);
        if (opts.prompt) url.searchParams.set('prompt', opts.prompt);
        if (opts.loginHint) url.searchParams.set('login_hint', opts.loginHint);
        return url.href;
    }

    /** Whether `url` is shaped like a callback to this app. Whether it is this tab's own is for
     * `handleCallback`, which refuses a missing transaction or another state, so a stale, replayed or
     * foreign callback ends in an error instead of an endless loading screen. */
    isCallback(url: URL): boolean {
        const redirect = new URL(this.settings.redirectUri);
        return url.origin === redirect.origin && url.pathname === redirect.pathname && url.searchParams.has('state') &&
            (url.searchParams.has('code') || url.searchParams.has('error'));
    }

    /** Validate the callback, exchange its code and keep the refresh token. The transaction is
     * consumed whatever the outcome, so a callback URL cannot be replayed. */
    async handleCallback(url: URL): Promise<CallbackResult> {
        const transaction = this.readTransaction();
        this.storage.removeItem(this.transactionKey);
        if (!transaction) throw new SessionCredentialError();
        const as = await this.discover();
        const params = oauth.validateAuthResponse(as, this.client, url, transaction.state);
        const response = await oauth.authorizationCodeGrantRequest(as, this.client, oauth.None(), params,
            this.settings.redirectUri, transaction.verifier, this.options());
        const result = await oauth.processAuthorizationCodeResponse(as, this.client, response,
            {expectedNonce: transaction.nonce, requireIdToken: true});
        return {credentials: this.keep(result), returnTo: transaction.returnTo};
    }

    /** Renew with the stored refresh token; the refreshed tokens keep the original roles scope,
     * so they name the same firm (design 4.6 item 6). */
    async renew(): Promise<TabCredentials> {
        const refreshToken = this.storage.getItem(this.refreshKey);
        if (!refreshToken) throw new SessionCredentialError();
        const as = await this.discover();
        const response = await oauth.refreshTokenGrantRequest(as, this.client, oauth.None(), refreshToken, this.options());
        return this.keep(await oauth.processRefreshTokenResponse(as, this.client, response));
    }

    /** Forget this tab's refresh token and pending transaction. */
    clear(): void {
        this.storage.removeItem(this.refreshKey);
        this.storage.removeItem(this.transactionKey);
    }

    private keep(result: oauth.TokenEndpointResponse): TabCredentials {
        if (!result.access_token || !result.id_token) throw new SessionCredentialError();
        if (result.refresh_token) this.storage.setItem(this.refreshKey, result.refresh_token);
        return {accessToken: result.access_token, idToken: result.id_token};
    }

    private readTransaction(): Transaction | undefined {
        const raw = this.storage.getItem(this.transactionKey);
        if (!raw) return undefined;
        try {
            const value = JSON.parse(raw) as Transaction;
            return typeof value?.state === 'string' && typeof value.verifier === 'string' &&
                typeof value.nonce === 'string' ? value : undefined;
        } catch {
            return undefined;
        }
    }
}
